// frontend/src/api.js
import axios from 'axios';
import { mensajeErrorApi } from './apiErrors';

export const API_BASE = '/api';

const TOKEN_KEY = 'ecuafly_token';
const USER_KEY = 'ecuafly_usuario';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getUsuario = () => localStorage.getItem(USER_KEY);
export const getRol = () => localStorage.getItem('ecuafly_rol');
export const getCasillero = () => localStorage.getItem('ecuafly_casillero');
export const getOnboarding = () => localStorage.getItem('ecuafly_onboarding') === 'true';

export function guardarSesion(token, usuario, rol, onboarding, codigoCasillero) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, usuario);
  if (rol) localStorage.setItem('ecuafly_rol', rol);
  if (onboarding !== undefined) localStorage.setItem('ecuafly_onboarding', onboarding);
  if (codigoCasillero) localStorage.setItem('ecuafly_casillero', codigoCasillero);
}

export function cerrarSesion() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem('ecuafly_rol');
  localStorage.removeItem('ecuafly_onboarding');
  localStorage.removeItem('ecuafly_casillero');
}

const api = axios.create({ baseURL: API_BASE });

// Chrome en Android a veces pierde el acceso a un archivo elegido desde Descargas o
// Drive entre que se selecciona y se envía (ERR_UPLOAD_FILE_CHANGED): la petición
// muere sin respuesta y reintentar con el mismo archivo falla igual. Por eso los
// archivos de un FormData se copian a memoria justo antes de enviar; si no se
// pueden leer, se avisa con un mensaje claro en vez de "sin conexión".
const MAX_COPIA_BYTES = 25 * 1024 * 1024;

async function copiarArchivosAMemoria(data) {
  if (typeof FormData === 'undefined' || typeof File === 'undefined' || !(data instanceof FormData)) return data;
  const entradas = Array.from(data.entries());
  if (!entradas.some(([, valor]) => valor instanceof File)) return data;
  const copia = new FormData();
  for (const [clave, valor] of entradas) {
    if (valor instanceof File && valor.size > 0 && valor.size <= MAX_COPIA_BYTES && typeof valor.arrayBuffer === 'function') {
      try {
        const contenido = await valor.arrayBuffer();
        copia.append(clave, new File([contenido], valor.name, { type: valor.type, lastModified: valor.lastModified }));
      } catch (e) {
        const err = new Error('No se pudo leer el archivo adjunto');
        err.archivoIlegible = true;
        throw err;
      }
    } else {
      copia.append(clave, valor);
    }
  }
  return copia;
}

// Mete el token en cada request. El backend exige autenticación en todos los
// endpoints salvo login/reset, así que sin esto todo es 401.
api.interceptors.request.use(async (config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Token ${token}`;
  if (config.data) {
    try {
      config.data = await copiarArchivosAMemoria(config.data);
    } catch (err) {
      err.config = config;   // para que el reporte de errores sepa qué petición era
      throw err;
    }
  }
  return config;
});

// --- Reporte automático de errores ---------------------------------------
// Un cliente que ve "No se pudo crear la prealerta" casi nunca avisa. Cada
// error que ve un usuario autenticado se manda a /errores/reportar/ para que
// aparezca en la pestaña "Errores" del panel (y por correo si es crítico).
// Va por fetch y no por `api` a propósito: así un fallo del reporte no se
// reporta a sí mismo en bucle.

const RECIENTES = new Map(); // huella -> timestamp: no repite el mismo error en ráfaga
const VENTANA_REPETIDOS_MS = 60 * 1000;
let paginaCerrandose = false;
if (typeof window !== 'undefined') {
  // Al recargar/cerrar la pestaña el navegador aborta las peticiones en vuelo
  // y axios las ve como "Network Error": no son un problema real.
  window.addEventListener('pagehide', () => { paginaCerrandose = true; });
  window.addEventListener('pageshow', () => { paginaCerrandose = false; });
}

function enviarReporte(payload) {
  const token = getToken();
  if (!token || paginaCerrandose) return;
  const huella = `${payload.origen}|${payload.metodo}|${payload.endpoint}|${payload.status_code}|${payload.mensaje_mostrado}`;
  const ahora = Date.now();
  if (ahora - (RECIENTES.get(huella) || 0) < VENTANA_REPETIDOS_MS) return;
  RECIENTES.set(huella, ahora);
  try {
    fetch(`${API_BASE}/errores/reportar/`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
      body: JSON.stringify({ ...payload, pagina: window.location.pathname + window.location.hash }),
    }).catch(() => {});
  } catch (e) {
    // Reportar es mejor-esfuerzo: nunca debe romper la app.
  }
}

// Solo metadatos (nombres de campos, nombre/tamaño/tipo de archivos): los
// valores pueden ser datos personales y no hacen falta para diagnosticar.
function resumenDelEnvio(data) {
  try {
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      const campos = [];
      const archivos = [];
      for (const [clave, valor] of data.entries()) {
        if (typeof File !== 'undefined' && valor instanceof File) {
          archivos.push({ campo: clave, nombre: valor.name, tipo: valor.type, tamano_kb: Math.round(valor.size / 1024) });
        } else {
          campos.push(clave);
        }
      }
      return { campos, archivos };
    }
    if (typeof data === 'string' && data.startsWith('{')) {
      return { campos: Object.keys(JSON.parse(data)) };
    }
  } catch (e) {
    // sin resumen
  }
  return {};
}

function reportarErrorHttp(err) {
  const config = err.config || {};
  const url = config.url || '';
  const metodo = (config.method || 'get').toUpperCase();
  const status = err.response ? err.response.status : 0;

  if (axios.isCancel(err) || url.includes('/errores/reportar/')) return;
  // 401: ya se maneja (vuelve al login). 500: el backend ya lo registra con
  // traceback, reportarlo acá lo duplicaría.
  if (status === 401 || status === 500) return;
  // Una lectura sin respuesta casi siempre es la conexión del usuario, no
  // nuestra: se reportan solo las acciones (POST/PATCH/...) que quedaron sin enviarse.
  if (status === 0 && metodo === 'GET') return;

  const data = err.response && err.response.data;
  const detalle = typeof data === 'string' ? data.slice(0, 600) : JSON.stringify(data || err.message || '').slice(0, 1000);

  enviarReporte({
    origen: 'frontend_http',
    status_code: status,
    metodo,
    endpoint: `${API_BASE}${url.startsWith('/') ? '' : '/'}${url}`,
    mensaje_mostrado: mensajeErrorApi(err, '(mensaje genérico del formulario)'),
    detalle,
    contexto: resumenDelEnvio(config.data),
  });
}

// Errores de JavaScript que rompen una pantalla sin que haya una petición de por medio.
export function instalarCapturaErroresJs() {
  window.addEventListener('error', (e) => {
    // Sin archivo propio = extensiones del navegador o scripts de terceros ("Script error.").
    if (!e.filename || !e.filename.startsWith(window.location.origin)) return;
    if (/ResizeObserver loop/i.test(e.message || '')) return;
    enviarReporte({
      origen: 'frontend_js', status_code: 0, metodo: '', endpoint: '',
      mensaje_mostrado: String(e.message || 'Error de JavaScript').slice(0, 300),
      detalle: String((e.error && e.error.stack) || `${e.filename}:${e.lineno}`).slice(0, 3000),
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    if (r && r.isAxiosError) return; // ya lo captura el interceptor de `api`
    enviarReporte({
      origen: 'frontend_js', status_code: 0, metodo: '', endpoint: '',
      mensaje_mostrado: `Promesa rechazada: ${(r && r.message) || r}`.slice(0, 300),
      detalle: String((r && r.stack) || '').slice(0, 3000),
    });
  });
}

// Errores de React capturados por ErrorBoundary: no llegan a window.onerror porque React los atrapa.
export function reportarErrorReact(error, info) {
  enviarReporte({
    origen: 'frontend_js', status_code: 0, metodo: '', endpoint: '',
    mensaje_mostrado: String((error && error.message) || 'Error de React').slice(0, 300),
    detalle: String(`${(error && error.stack) || ''}
${(info && info.componentStack) || ''}`).slice(0, 3000),
  });
}

// Un 401 significa token vencido o borrado: limpiamos y recargamos para
// volver al login, en vez de dejar la pantalla a medias con errores sueltos.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && getToken()) {
      cerrarSesion();
      window.location.reload();
    }
    try { reportarErrorHttp(err); } catch (e) { /* mejor-esfuerzo */ }
    return Promise.reject(err);
  }
);

export async function login(username, password) {
  // El login va por axios pelado: todavía no hay token que mandar.
  const res = await axios.post(`${API_BASE}/auth/login/`, { username, password });
  guardarSesion(
    res.data.token,
    res.data.email || username,
    res.data.rol,
    res.data.onboarding_completado,
    res.data.codigo_casillero
  );
  return res.data;
}

export default api;
